<template>
  <div class="shipping-group-total">
    <shipping-group-total-item
      :price="totalPrice"
      class="shipping-group-total__header"
      @click.native="isExpanded = !isExpanded"
    >
      <template #label>
        <checkout-label
          :title="`Заказ ${index + 1}`"
          :type="group.type"
          class="shipping-group-total__order-label"
        />
      </template>

      <template #append>
        <chevron-animated
          :reverse="isExpanded"
          class="shipping-group-total__chevron"
        />
      </template>
    </shipping-group-total-item>

    <transition name="fade">
      <div v-if="isExpanded" class="shipping-group-total__content">
        <div v-if="deliveryInfo" class="shipping-group-total__delivery">
          <span>{{ deliveryInfo.text }}</span>
          <span
            v-if="deliveryInfo.date"
            class="shipping-group-total__delivery-date"
          >
            {{ deliveryInfo.date }}
          </span>
        </div>

        <shipping-group-total-item
          :price="productsPrice - discount.total"
          :old-price="productsPrice"
          :label="productAmountText"
        />

        <shipping-group-total-item
          :price="shippingPrice"
          label="Доставка"
        />

        <shipping-group-total-item
          v-if="discount.visible"
          :price="-discount.visible"
          label="Скидка"
          class="shipping-group-total__discount"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { getDeliverySelectedDate } from '@/components/checkout/checkout-total-widget/utils';
import { pluralize } from '@/utils/string-utils';
import { dateToVerbal } from '@/utils/date-to-string';
import getIntervalDates from '@mixins/get-interval-dates';

import ChevronAnimated from '@icons/chevron-animated.vue';
import CheckoutLabel from '@/components/checkout/checkout-label.vue';
import ShippingGroupTotalItem from '@/components/checkout/checkout-total-widget/shipping-group-total-item.vue';

const includeDiscounts = ['ware_discount', 'promotion', 'promocode', 'bonus'];

export default {
  name: 'shipping-group-total',

  components: {
    CheckoutLabel,
    ShippingGroupTotalItem,
    ChevronAnimated,
  },

  mixins: [getIntervalDates],

  props: {
    index: {
      type: Number,
      default: 0,
    },
    group: {
      type: Object,
      required: true,
    },
    thirdpartyStore: {
      type: Object,
      default: null,
    }
  },

  data: () => ({
    isExpanded: true,
  }),

  computed: {
    productAmount() {
      return this.group.skus.reduce((res, { quantity }) => res += quantity, 0);
    },

    productAmountText() {
      return pluralize(this.productAmount, ['товар', 'товара', 'товаров']);
    },

    deliveryInfo() {
      const { type, deliveryInfo, receivedInterval } = this.group;

      const getDate = ({ dateFrom, dateTo } = {}) => {
        return dateFrom && dateTo
          ? this.getIntervalDates(dateFrom, dateTo)
          : dateToVerbal(new Date(dateFrom || dateTo))
      }

      switch (type) {
        case 'unallocated':
          return {
            text: 'Не выбран способ получения',
          };
        case 'delivery':
          return {
            text: `Доставить ${this.productAmountText} курьером`,
            date: deliveryInfo && getDate(getDeliverySelectedDate(deliveryInfo.interval)),
          };
        case 'pickup':
          return {
            text: `Забрать ${this.productAmountText} из магазина`,
            date: receivedInterval && getDate({ dateFrom: receivedInterval.from, dateTo: receivedInterval.to }),
          };
        case 'thirdparty':
         return {
           text: this.getThirdpartyText(),
           date: receivedInterval && getDate({ dateFrom: receivedInterval.from, dateTo: receivedInterval.to }),
         }
        default:
          return null;
      }
    },

    productsPrice() {
      return +this.group.subtotal?.productsPrice || 0;
    },

    totalPrice() {
      return +this.group.subtotal?.totalPrice || 0;
    },

    shippingPrice() {
      const price = +this.group.subtotal?.shippingPrice || 0;

      if (price) return price;

      if (this.group.type === 'unallocated') return 'Не выбрана';

      return 'Бесплатно';
    },

    discount() {
      return this.group.subtotal?.discounts?.reduce((res, { value, type }) => {
        const discount =  +value || 0;
        if (includeDiscounts.includes(type)) res.visible += discount;
        res.total += discount;
        return res;
      }, {
        visible: 0,
        total: 0,
      });
    },
  },

  methods: {
    getThirdpartyText() {
      let thirdpartyName = '';

      switch (this.thirdpartyStore?.thirdPartyStoreType) {
        case 'postamat':
          thirdpartyName = 'из постамата';
          break;
        case 'pvz':
          thirdpartyName = 'из ПВЗ';
          break;
        default:
          thirdpartyName = 'в пункте самовывоза';
          break;
      }

      return `Забрать ${this.productAmountText} ${thirdpartyName}`;
    }
  }
};
</script>

<style lang="scss">
.shipping-group-total {
  padding: 12px;
  box-sizing: border-box;
  border: 1px solid $blue_8;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .04), 0 8px 24px rgba(0, 0, 0, .03), 0 16px 32px rgba(0, 0, 0, .02);
  font-size: 12px;
  line-height: 16px;
  color: $black;

  &__header {
    cursor: pointer;

    &.shipping-group-total-item {
      margin-bottom: 0;
    }

    .shipping-group-total-item__price {
      font-weight: 600;
    }
  }

  &__chevron {
    width: 12px;
    height: 12px;
    margin-left: 4px;
    color: $gray_l2;
  }

  &__order-label {
    height: 20px;
    min-width: 67px;
    width: auto;
    padding: 2px 8px 2px 6px;
    font-size: 11px;
    line-height: 16px;
    font-weight: 700;

    svg {
      width: 12px;
      height: 12px;
      margin-right: 3px;
    }
  }

  &__delivery {
    display: flex;
    flex-direction: column;
    margin-bottom: 2px;
  }

  &__delivery-date {
    margin-top: 2px;
    color: $blue;
  }

  &__content {
    margin-top: 8px;
  }

  &__discount {
    .shipping-group-total-item {
      &__label,
      &__price {
        color: $red;
      }
    }
  }
}

@include breakpoint("desktop") {
  .shipping-group-total {
    padding: 6px;

    &__content {
      padding: 0 10px;
      margin-bottom: 12px;
    }
  }
}
</style>
